import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { AlertTriangle, Clock, CalendarCheck, Search, Receipt, Send, MoreHorizontal, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Scadenze della fatturazione telefonia (backend: GET /telecom/scadenze, stessi dati di
// Fatturazione). Le scadute stanno in cima, in rosso; il loro numero è anche nel menu laterale.
const FASCE = {
  scaduta: { label: 'Scadute', riga: 'bg-red-50 border-l-4 border-l-red-500', testo: 'text-red-700' },
  in_scadenza: { label: 'In scadenza (30 giorni)', riga: 'bg-amber-50 border-l-4 border-l-amber-400', testo: 'text-amber-700' },
  futura: { label: 'Prossime', riga: 'border-l-4 border-l-transparent', testo: 'text-slate-600' },
};

const FILTRI = [
  { key: 'urgenti', label: 'Scadute e in scadenza' },
  { key: 'scaduta', label: 'Solo scadute' },
  { key: 'tutte', label: 'Tutte' },
];

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-');
const formatCurrency = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

const quando = (giorni) => {
  if (giorni < 0) return `scaduta da ${-giorni} ${giorni === -1 ? 'giorno' : 'giorni'}`;
  if (giorni === 0) return 'scade oggi';
  return `tra ${giorni} ${giorni === 1 ? 'giorno' : 'giorni'}`;
};

const TelecomScadenze = () => {
  const [scadenze, setScadenze] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('urgenti');
  const [cerca, setCerca] = useState('');
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/telecom/scadenze`);
      setScadenze(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle scadenze');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // il badge nel menu laterale si aggiorna subito dopo un pagamento
  const aggiorna = () => {
    fetchData();
    window.dispatchEvent(new Event('jvservice:scadenze-telefonia'));
  };

  const handleMarkPaid = async (s, generateNext) => {
    try {
      const res = await axios.post(`${API}/telecom/billing/${s.id}/mark-paid?generate_next=${generateNext}`);
      toast.success(res.data.next_billing_generated
        ? 'Fattura pagata - Prossima fattura generata automaticamente'
        : 'Fattura marcata come pagata');
      aggiorna();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleSendEmail = async (s) => {
    try {
      const res = await axios.post(`${API}/telecom/billing/${s.id}/send-email`);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio email');
    }
  };

  const totali = Object.fromEntries(Object.keys(FASCE).map((f) => {
    const righe = scadenze.filter((s) => s.fascia === f);
    return [f, { n: righe.length, importo: righe.reduce((t, s) => t + (s.amount || 0), 0) }];
  }));

  const parole = cerca.toLowerCase().split(/\s+/).filter(Boolean);
  const visibili = scadenze.filter((s) => {
    if (filtro === 'urgenti' && s.fascia === 'futura') return false;
    if (filtro === 'scaduta' && s.fascia !== 'scaduta') return false;
    const testo = `${s.client_name} ${s.phone_number} ${s.operator} ${s.client_phone}`.toLowerCase();
    return parole.every((p) => testo.includes(p));
  });

  const riquadri = [
    { fascia: 'scaduta', icona: AlertTriangle, iconaColore: 'text-red-600', card: 'bg-red-50 border-red-200' },
    { fascia: 'in_scadenza', icona: Clock, iconaColore: 'text-amber-600', card: 'bg-amber-50 border-amber-200' },
    { fascia: 'futura', icona: CalendarCheck, iconaColore: 'text-slate-500', card: 'bg-white border-slate-200' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="telecom-scadenze-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Scadenze
          </h1>
          <p className="text-slate-600 mt-1">Fatture della telefonia da pagare, in ordine di scadenza</p>
        </div>
        <Button variant="outline" onClick={aggiorna} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
        </Button>
      </div>

      {totali.scaduta.n > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-red-300 bg-red-50 text-red-800">
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">
              {totali.scaduta.n} {totali.scaduta.n === 1 ? 'fattura scaduta' : 'fatture scadute'} non pagate — {formatCurrency(totali.scaduta.importo)}
            </p>
            <p className="text-sm">Sono in cima all'elenco, evidenziate in rosso.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {riquadri.map(({ fascia, icona: Icona, iconaColore, card }) => (
          <Card key={fascia} className={`border ${card}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <Icona className={`w-8 h-8 ${iconaColore}`} />
              <div>
                <p className="text-sm text-slate-600">{FASCE[fascia].label}</p>
                <p className="text-2xl font-bold text-slate-900">{totali[fascia].n}</p>
                <p className="text-sm text-slate-500">{formatCurrency(totali[fascia].importo)}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div className="relative md:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input className="pl-9" placeholder="Cerca cliente o numero..." value={cerca} onChange={(e) => setCerca(e.target.value)} />
          </div>
          <div className="flex gap-2">
            {FILTRI.map((f) => (
              <Button key={f.key} size="sm" variant={filtro === f.key ? 'default' : 'outline'} onClick={() => setFiltro(f.key)}>
                {f.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Scadenza</th>
              <th className="text-left px-4 py-2 font-medium">Cliente</th>
              <th className="text-left px-4 py-2 font-medium">Numero / Operatore</th>
              <th className="text-left px-4 py-2 font-medium">Periodo</th>
              <th className="text-right px-4 py-2 font-medium">Importo</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Caricamento...</td></tr>
            ) : visibili.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Nessuna scadenza</td></tr>
            ) : visibili.map((s) => (
              <tr key={s.id} className={`border-t border-slate-900 ${FASCE[s.fascia].riga}`}>
                <td className="px-4 py-2">
                  <div className="font-medium text-slate-900">{formatDate(s.due_date)}</div>
                  <div className={`text-xs font-medium ${FASCE[s.fascia].testo}`}>{quando(s.giorni)}</div>
                </td>
                <td className="px-4 py-2">
                  <button
                    type="button"
                    className="font-medium text-slate-900 hover:text-violet-700 hover:underline text-left"
                    onClick={() => navigate(`/telecom/billing?fattura=${s.id}`)}
                    title="Apri in Fatturazione"
                  >
                    {s.client_name || '-'}
                  </button>
                  {s.client_phone && <div className="text-xs text-slate-500">{s.client_phone}</div>}
                </td>
                <td className="px-4 py-2">
                  <div className="font-mono text-slate-800">{s.phone_number || '-'}</div>
                  <div className="text-xs text-slate-500">{s.operator}</div>
                </td>
                <td className="px-4 py-2 text-slate-600">
                  {s.period_from ? `${formatDate(s.period_from)} – ${formatDate(s.period_to)}` : '-'}
                </td>
                <td className="px-4 py-2 text-right font-semibold text-violet-700">{formatCurrency(s.amount)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <Button
                    size="sm" variant="outline"
                    className="h-7 px-2 text-xs border-amber-300 text-amber-700 hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700"
                    onClick={() => handleMarkPaid(s, true)}
                    title="Segna come pagata e genera la fattura successiva"
                  >
                    Pagato →
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 ml-1"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleMarkPaid(s, true)}>
                        <Receipt className="w-4 h-4 mr-2" />Pagato + Genera Prossima
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleMarkPaid(s, false)}>
                        <Receipt className="w-4 h-4 mr-2" />Solo Pagato
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleSendEmail(s)}>
                        <Send className="w-4 h-4 mr-2" />Invia Email
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TelecomScadenze;
